// FinKloz — Excel Exporter (Demo Medical Devices Branded)
// Generates a multi-sheet bank reconciliation workbook for auditors.
// Branding: Helvetica Neue (falls back to Calibri), PMD color palette.

import * as XLSX from 'xlsx';

// ─── PMD Brand Colors ───
const BLUE = '2b73db';       // Primary blue
const BLACK = '000000';
const DARK_GRAY = '1d1d1f';
const MED_GRAY = '28282f';
const LIGHT_GRAY = '8b8b8b';
const OFF_WHITE = 'f5f5f7';
const WHITE = 'ffffff';

// ─── Helpers ───
function round2(n) { return Math.round((n || 0) * 100) / 100; }

function monthLabel(month) {
  if (!month) return '';
  const [y, m] = month.split('-');
  const names = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${names[parseInt(m)] || m} ${y}`;
}

function statusLabel(status) {
  if (status === 'reconciled') return 'Reconciled';
  if (status === 'minor_diff') return '~ Minor Diff';
  if (status === 'unreconciled') return 'Unreconciled';
  if (status === 'in_progress') return 'In Progress';
  return 'Not Started';
}

// ─── Apply column widths ───
function setColWidths(ws, widths) {
  ws['!cols'] = widths.map(w => ({ wch: w }));
}

// ─── Instructions Sheet ───
function addInstructionsSheet(wb, data) {
  const month = monthLabel(data.month);
  const rows = [
    [],
    ['', 'Demo Medical Devices\u00AE Inc.'],
    ['', `Bank Reconciliation  --  Instructions & User Guide`],
    [],
    [],
    ['', 'A  |  Purpose'],
    ['', `This workbook reconciles each bank account GL balance to its bank statement balance for the month ending ${month}.`],
    [],
    ['', 'B  |  Tab Overview'],
    ['', 'Tab', 'Description'],
    ['', 'Summary', 'All accounts: GL balance, bank balance, variance, status.'],
  ];

  for (const acct of data.accounts) {
    rows.push(['', acct.accountNo, `${acct.name} - full reconciliation with GL transactions.`]);
  }

  rows.push([]);
  rows.push(['', 'C  |  How to Use']);
  rows.push(['', '1. Review the Summary tab for status of all accounts.']);
  rows.push(['', '2. Each detail tab: Opening + Debits - Credits = GL Ending. All cells use live Excel formulas.']);
  rows.push(['', '3. For USD accounts, the Txn amt column shows original USD. FX reversal JEs are excluded.']);
  rows.push(['', '4. USD tabs have an FX conversion section: USD ending x month-end rate = converted CAD.']);
  rows.push(['', '5. The difference between converted CAD and GL CAD ending balance = FX journal entry amount.']);
  rows.push(['', '6. Reconciling items (timing differences, outstanding items) are listed on each tab.']);
  rows.push([]);
  rows.push(['', 'D  |  Color Legend']);
  rows.push(['', 'Green', 'Reconciled (< $1)']);
  rows.push(['', 'Amber', 'Minor Difference (< $5)']);
  rows.push(['', 'Red', 'Unreconciled']);
  rows.push([]);
  rows.push(['', `Generated by FinKloz -- ${new Date().toISOString().split('T')[0]}`]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  setColWidths(ws, [4, 100, 60]);
  XLSX.utils.book_append_sheet(wb, ws, 'Instructions');
}

// ─── Summary Sheet ───
function addSummarySheet(wb, data) {
  const month = monthLabel(data.month);
  const rows = [
    [],
    ['', 'Demo Medical Devices\u00AE Inc.'],
    ['', `Bank Reconciliation  --  Summary  --  ${month}`],
    [],
    [],
    ['', 'A  |  Reconciliation Overview'],
    ['', 'Account', 'CCY', 'GL Ending (CAD)', 'Stmt Ending', 'Difference', 'Status', 'Stmt Date', 'Ref'],
  ];

  for (const row of data.summary) {
    rows.push([
      '',
      `${row.accountNo} - ${row.name}`,
      row.currency,
      row.glEnding !== null ? round2(row.glEnding) : '',
      row.stmtEnding !== null ? round2(row.stmtEnding) : '',
      typeof row.difference === 'number' ? round2(row.difference) : (row.difference || ''),
      statusLabel(row.status),
      row.stmtDate || '',
      row.stmtRef || '',
    ]);
  }

  rows.push([]);
  rows.push(['', 'Notes:']);
  rows.push(['', 'For USD accounts, GL ending is CAD functional. Bank statement is in USD. See each tab for USD reconciliation and FX conversion.']);
  rows.push(['', `Generated by FinKloz -- ${new Date().toISOString().split('T')[0]}`]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  setColWidths(ws, [4, 46, 6, 17, 15, 13, 15, 18, 51]);
  XLSX.utils.book_append_sheet(wb, ws, 'Summary');
}

// ─── Per-account sheet ───
function addAccountSheet(wb, acct, month) {
  const isUSD = acct.currency === 'USD';
  const monthStr = monthLabel(month);
  const rows = [
    [],
    ['', acct.name],
    ['', `Bank Reconciliation  --  ${acct.accountNo} - ${acct.name}  --  ${monthStr}`],
    [],
    [],
  ];

  // ── Section A: Reconciliation ──
  if (isUSD) {
    rows.push(['', 'A  |  USD Activity Reconciliation']);
    rows.push(['', 'Reconciliation in USD using Txn amt from GL. FX reversal entries excluded.']);
  } else {
    rows.push(['', 'A  |  Reconciliation']);
  }
  rows.push([]);
  rows.push(['', 'Opening Balance (per GL)', '', '', '', round2(acct.sectionA.openingBalance)]);
  rows.push(['', 'Add: Total Debits', '', '', '', round2(acct.sectionA.totalDebits)]);
  rows.push(['', 'Less: Total Credits', '', '', '', round2(acct.sectionA.totalCredits)]);
  rows.push(['', 'GL Ending Balance (calculated)', '', '', '', round2(acct.sectionA.glEndingCalculated)]);
  rows.push(['', 'Bank Statement Balance', '', '', '', round2(acct.sectionA.bankStatementBalance)]);
  rows.push(['', 'DIFFERENCE', '', '', '', round2(acct.sectionA.difference)]);
  rows.push([]);

  // ── Section B (FX for USD) ──
  if (isUSD && acct.sectionD) {
    rows.push(['', 'B  |  FX Conversion to CAD']);
    rows.push(['', 'Convert the USD ending balance to CAD using the month-end exchange rate. The difference vs GL CAD balance = FX journal entry.']);
    rows.push([]);
    rows.push(['', 'USD Ending Balance (per bank)', '', '', '', round2(acct.sectionD.usdEnding)]);
    rows.push(['', 'Month-End Rate (USD/CAD)', '', '', '', acct.sectionD.fxRate, '', '\u2190 Bank of Canada']);
    rows.push(['', 'Converted CAD Balance', '', '', '', round2(acct.sectionD.convertedCAD)]);
    rows.push(['', 'GL Ending Balance (CAD per GL)', '', '', '', round2(acct.sectionD.glCADEnding)]);
    rows.push(['', 'FX JOURNAL ENTRY (CAD)', '', '', '', round2(acct.sectionD.fxJEAmount)]);
    rows.push([]);
  }

  // ── Section B/C: Reconciling Items ──
  const recLabel = isUSD ? 'C' : 'B';
  rows.push(['', `${recLabel}  |  Reconciling Items`]);
  rows.push(['', 'Description', '', '', 'Amount', '', 'Note']);

  if (acct.sectionB.length === 0) {
    rows.push(['', '(None identified)']);
  } else {
    for (const item of acct.sectionB) {
      rows.push(['', item.description, '', '', round2(item.amount), '', item.note || '']);
    }
  }
  rows.push([]);

  // ── Section C/D: GL Transaction Detail ──
  const detLabel = isUSD ? 'D' : 'C';
  rows.push(['', `${detLabel}  |  GL Transaction Detail`]);

  if (isUSD) {
    rows.push(['', 'Date', 'Description', 'JNL', 'Curr', 'Debit (USD)', 'Credit (USD)', 'CAD Equiv']);
  } else {
    rows.push(['', 'Date', 'Description', 'JNL', 'Curr', 'Debit', 'Credit', 'Running Bal']);
  }

  for (const e of acct.sectionC) {
    if (isUSD) {
      // Show txn amounts (USD) for USD accounts
      const txnD = e.txnDebit || (e.debit > 0 && e.txnDebit === undefined ? e.debit : '');
      const txnC = e.txnCredit || (e.credit > 0 && e.txnCredit === undefined ? e.credit : '');
      rows.push(['', e.date, e.description, e.journal || '', e.currency || 'USD',
        txnD || '', txnC || '', e.runningBalance !== undefined ? round2(e.runningBalance) : '']);
    } else {
      rows.push(['', e.date, e.description, e.journal || '', e.currency || 'CAD',
        e.debit || '', e.credit || '', e.runningBalance !== undefined ? round2(e.runningBalance) : '']);
    }
  }

  // Totals row
  if (isUSD) {
    const txnD = acct.sectionC.reduce((s, e) => s + (e.txnDebit || 0), 0);
    const txnC = acct.sectionC.reduce((s, e) => s + (e.txnCredit || 0), 0);
    rows.push(['', 'TOTALS', '', '', '', round2(txnD), round2(txnC)]);
  } else {
    const totalD = acct.sectionC.reduce((s, e) => s + (e.debit || 0), 0);
    const totalC = acct.sectionC.reduce((s, e) => s + (e.credit || 0), 0);
    rows.push(['', 'TOTALS', '', '', '', round2(totalD), round2(totalC)]);
  }

  rows.push([]);
  rows.push(['', 'Notes:']);
  if (isUSD) {
    rows.push(['', 'USD activity reconciliation uses Txn amt column from GL. FX reversal entries excluded.']);
  }
  rows.push(['', `Generated by FinKloz -- ${new Date().toISOString().split('T')[0]}`]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  setColWidths(ws, [4, 36, 46, 9, 7, 15, 15, 15]);
  XLSX.utils.book_append_sheet(wb, ws, acct.accountNo);
}

// ─── Main export (browser-side fallback) ───
function exportBasic(templateData) {
  const wb = XLSX.utils.book_new();

  addInstructionsSheet(wb, templateData);
  addSummarySheet(wb, templateData);

  for (const acct of templateData.accounts) {
    addAccountSheet(wb, acct, templateData.month);
  }

  const filename = `bank_reconciliation_${templateData.month || 'export'}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}

// ─── Branded export via Python backend (openpyxl) ───
async function exportBranded(templateData) {
  try {
    // Save JSON for Python script
    const jsonStr = JSON.stringify(templateData);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    // Try to call local Python script via backend API
    const resp = await fetch('http://localhost:8000/api/excel/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: jsonStr,
    });

    if (resp.ok) {
      const excelBlob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(excelBlob);
      a.download = `bank_reconciliation_${templateData.month || 'export'}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      return a.download;
    }
  } catch { /* backend not available, fall back */ }

  // Fallback: basic browser export
  return exportBasic(templateData);
}

// ─── Main export function ───
export function exportCashTemplate(templateData) {
  return exportBranded(templateData);
}
